import { defineStyle, defineStyleConfig } from '@chakra-ui/react'

import { responsiveHover } from '../../utils'

const baseStyle = defineStyle(props => {
	const { colorScheme: color } = props

	return {
		'*': {
			pointerEvents: 'none'
		},
		p: 0,
		h: 'auto',
		border: 'none',
		lineHeight: 'normal',
		verticalAlign: 'baseline',
		color: color === 'primary' ? 'primary.100' : `${color}.200`,
		textDecoration: 'underline',
		textDecorationThickness: '0.125rem',
		textDecorationSkipInk: 'none',
		textUnderlineOffset: '0.1875rem', // 3px
		...responsiveHover({
			color: 'gray.300',
			textDecorationThickness: '0.125rem',
			textUnderlineOffset: '0.1875rem' // 3px
		}),
		_active: {
			color: 'gray.500',
			textDecorationThickness: '0.125rem',
			textUnderlineOffset: '0.1875rem' // 3px
		}
	}
})

const variantSolid = defineStyle(props => {
	const { colorScheme: color } = props
	const common = defineStyle({
		border: 'none',
		borderRadius: 'md',
		textDecoration: 'none',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center'
	})

	if (color === 'brand') {
		return {
			...common,
			bg: 'brand.200',
			color: 'white',
			...responsiveHover({
				bg: 'brand.100',
				color: 'white',
				textDecoration: 'none',
				_disabled: {
					bg: 'gray.100'
				}
			}),
			_active: {
				textDecoration: 'none',
				color: 'white',
				bg: 'brand.300',
				_disabled: {
					bg: 'gray.100'
				}
			},
			_disabled: {
				textDecoration: 'none',
				bg: 'gray.100',
				color: 'gray.300'
			},
			_loading: {
				textDecoration: 'none',
				_disabled: {
					bg: 'brand.200',
					color: 'white'
				}
			}
		}
	}

	return {
		...common,
		bg: `${color}.200`,
		...responsiveHover({
			textDecoration: 'none',
			bg: `${color}.100`,
			_disabled: {
				bg: 'gray.100'
			}
		}),
		_active: {
			textDecoration: 'none',
			bg: `${color}.300`,
			_disabled: {
				bg: 'gray.100'
			}
		},
		_disabled: {
			textDecoration: 'none',
			bg: 'gray.100',
			color: 'gray.300'
		},
		_loading: {
			textDecoration: 'none',
			_disabled: {
				bg: `${color}.200`,
				color: 'white'
			}
		}
	}
})

const variantOutline = defineStyle(props => {
	const { colorScheme: color } = props

	const common = defineStyle({
		border: 'none',
		borderWidth: '0.0625rem',
		borderRadius: 'md',
		borderStyle: 'solid',
		textDecoration: 'none',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		bg: 'white',
		...responsiveHover({
			textDecoration: 'none',
			_disabled: {
				bg: 'gray.100',
				color: 'gray.300',
				borderColor: 'gray.200',
				shadow: 'none'
			}
		}),
		_active: {
			bg: `${color}.200`,
			color: 'white',
			borderColor: `${color}.100`,
			shadow: 'none',
			textDecoration: 'none',
			_disabled: {
				bg: 'gray.100',
				color: 'gray.300',
				borderColor: 'gray.200'
			},
			_loading: {
				_disabled: {
					borderColor: `${color}.200`
				}
			}
		},
		_disabled: {
			bg: 'gray.100',
			color: 'gray.300'
		},
		_loading: {
			_disabled: {
				bg: 'white',
				color: `${color}.200`
			}
		}
	})

	if (color === 'gray') {
		return {
			...common,
			color: 'gray.500',
			borderColor: 'gray.200',
			...responsiveHover({
				...common['@media (hover: hover) and (pointer: fine)']._hover,
				bg: 'gray.100',
				color: 'gray.500',
				shadow: 'gray-glow'
			}),
			_active: {
				...common._active,
				bg: 'white',
				color: 'brand.300',
				borderColor: 'currentColor',
				_disabled: {
					bg: 'gray.100',
					color: 'gray.300',
					borderColor: 'gray.200'
				},
				_loading: {
					_disabled: {
						borderColor: 'currentColor'
					}
				}
			},
			_loading: {
				_disabled: {
					color: 'gray.400',
					borderColor: 'currentColor'
				}
			}
		}
	}

	return {
		...common,
		color: `${color}.200`,
		borderColor: 'currentColor',
		...responsiveHover({
			...common['@media (hover: hover) and (pointer: fine)']._hover,
			bg: 'white',
			color: `${color}.300`,
			shadow: 'glow'
		}),
		_active: {
			...common._active,
			bg: `${color}.200`,
			color: 'white',
			borderColor: `${color}.100`,
			shadow: 'none'
		},
		_disabled: {
			bg: 'gray.100',
			color: 'gray.300',
			borderColor: 'gray.200'
		},
		_loading: {
			_disabled: {
				bg: 'white',
				color: `${color}.200`,
				borderColor: 'currentColor'
			}
		}
	}
})

const variantMenu = defineStyle({
	textDecoration: 'none',
	borderStyle: 'solid',
	borderTopWidth: '0.0625rem',
	borderBottomWidth: '0.0625rem',
	borderLeftWidth: '0.125rem',
	borderRightWidth: '0.125rem',
	borderColor: 'transparent',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	width: '100%',
	bg: 'none',
	borderRadius: 0,
	gap: '0.5rem',
	px: '1rem',
	color: 'black',
	_active: {
		textDecoration: 'none',
		color: 'black',
		borderColor: 'brand.200'
	},
	_disabled: {
		textDecoration: 'none',
		bg: 'gray.100',
		color: 'gray.200'
	},
	...responsiveHover({
		bg: 'gray.100',
		color: 'black',
		textDecoration: 'none',
		_disabled: {
			bg: 'gray.100',
			color: 'gray.200'
		}
	})
})

const variantSolidNoBg = defineStyle({
	color: 'gray.500',
	textDecoration: 'none',
	...responsiveHover({
		textDecoration: 'none',
		color: 'brand.200'
	}),
	_active: {
		textDecoration: 'none',
		color: 'brand.300'
	},
	_disabled: {
		textDecoration: 'none',
		color: 'gray.300'
	}
})

const variantUnstyled = defineStyle({
	bg: 'none',
	border: 'none',
	px: '0',
	fontWeight: 'normal',
	color: 'inherit',
	textDecoration: 'none',
	...responsiveHover({
		textDecoration: 'none',
		color: 'inherit'
	})
})

const variants = {
	solid: variantSolid,
	outline: variantOutline,
	menu: variantMenu,
	'solid-no-bg': variantSolidNoBg,
	unstyled: variantUnstyled
}

const sizes = {
	sm: defineStyle({
		h: '2.5rem', // 40px
		px: '1.5rem', // 24px
		borderWidth: '0.0625rem'
	}),
	md: defineStyle({
		h: '3rem', // 48px
		px: '1.5rem', // 24px
		borderWidth: '0.125rem'
	}),
	lg: defineStyle({
		fontSize: 'md',
		h: '4rem', // 64px
		px: '2rem', // 32px
		borderWidth: '0.125rem'
	}),
	icon: defineStyle({
		w: '4rem', // 64px
		h: '4rem' // 64px
	}),
	'icon-medium': defineStyle({
		w: '3rem', // 48px
		h: '3rem', // 48px
		borderRadius: 'sm'
	}),
	'icon-small': defineStyle({
		w: '2.5rem', // 40px
		h: '2.5rem', // 40px
		borderRadius: '0.5rem'
	}),
	tab: defineStyle({
		h: '6rem', // 96px
		w: '7.5rem', // 120px
		borderRadius: 'md',
		fontWeight: 'normal'
	}),
	'tab-small': defineStyle({
		h: '3rem', // 48px
		px: '1.5rem', // 24px
		borderRadius: 'sm',
		fontWeight: 'medium'
	})
}

export const linkTheme = defineStyleConfig({
	baseStyle,
	variants,
	sizes,
	defaultProps: {
		colorScheme: 'primary'
	}
})
